/**
 * The rock tint by grade and signature (M8 MC-a6b, D-512's deferred half): a per-instance colour multiplier on the
 * field's rocks (three's `instanceColor`, which every node material multiplies into its diffuse colour on both the
 * WebGPU and the WebGL2 backend), so a field's grade and the system's signature ore read at a glance.
 *
 * - **Brightness by grade:** `1 + brightnessPerGrade × (grade − 1)` on all three channels, where `grade` is the field
 *   grade multiplier the rock was composed with (`galaxy.json minerals.grades.mult`: poor 0.7, standard 1, rich 1.4,
 *   pristine 1.8). A rock outside every field (grade 0) keeps 1.
 * - **Hue by signature:** a rock whose field holds the system's signature ore leans `signatureHue` of the way toward
 *   that ore's `wares.json tint` (linear, normalised to luminance 1, so the nudge moves hue and saturation but not
 *   brightness).
 *
 * `view.json tint` holds both numbers; 0 and 0 is neutral: the view attaches no instance colour at all (today's look
 * and shader, exactly). `rockTint` is allocation-free (D-014).
 */
import { Color } from 'three/webgpu'

export interface RockTintConfig {
  /** Brightness change per unit of grade multiplier above 1 (0 = off). */
  readonly brightnessPerGrade: number
  /** How far (0..1) a signature rock leans toward its ore's luminance-normalised tint (0 = off). */
  readonly signatureHue: number
}

/** True when the tint can change any rock (else the view attaches no instance colour). */
export function tintEnabled(cfg: RockTintConfig): boolean {
  return cfg.brightnessPerGrade > 0 || cfg.signatureHue > 0
}

/** Rec. 709 luminance weights (linear RGB). */
const LUMA_R = 0.2126
const LUMA_G = 0.7152
const LUMA_B = 0.0722

/**
 * Each ware's `tint` (sRGB hex) as linear RGB normalised to luminance 1, 3 floats per ware index. Load-time
 * (allocates). A black tint keeps white (no hue).
 */
export function signatureColors(tintHex: readonly number[]): Float32Array {
  const out = new Float32Array(tintHex.length * 3)
  const c = new Color()
  for (let w = 0; w < tintHex.length; w++) {
    c.setHex(tintHex[w])
    const l = LUMA_R * c.r + LUMA_G * c.g + LUMA_B * c.b
    const o = w * 3
    out[o] = l > 0 ? c.r / l : 1
    out[o + 1] = l > 0 ? c.g / l : 1
    out[o + 2] = l > 0 ? c.b / l : 1
  }
  return out
}

/**
 * The tint of a rock with field `grade` (0: outside every field) and `signature` (ware index, -1: none) into
 * `out[o..o+2]`. Identity (1, 1, 1) when neutral, outside every field and without a signature. Allocation-free.
 */
export function rockTint(grade: number, signature: number, cfg: RockTintConfig, colors: Float32Array, out: Float32Array, o: number): void {
  const b = grade > 0 ? Math.max(0, 1 + cfg.brightnessPerGrade * (grade - 1)) : 1
  let r = b
  let g = b
  let bl = b
  const h = cfg.signatureHue
  const s = signature * 3
  if (h > 0 && signature >= 0 && s + 2 < colors.length) {
    r *= 1 + h * (colors[s] - 1)
    g *= 1 + h * (colors[s + 1] - 1)
    bl *= 1 + h * (colors[s + 2] - 1)
  }
  out[o] = r
  out[o + 1] = g
  out[o + 2] = bl
}
